import { matchesBusinessName } from "@dehub/theme";
import { Page } from "../../components/layout/Page";
import {
  Box,
  Button,
  Input,
  Paper,
  Skeleton,
  Text,
  Title,
  UnstyledButton,
} from "@mantine/core";
import {
  FileTextIcon,
  ListMagnifyingGlassIcon,
  PaperPlaneTiltIcon,
  PhoneIcon,
  PlusIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { ARApi, NetworkSettlementApi } from "../../apis";
import { DocNoCell, ListStates, RowActions } from "../../components/list";
import { usePermission } from "../../components/permission";
import { filterInputStyles } from "../../components/ui/shared-styles";
import { IAuth } from "../../interfaces/IAuth";
import { currencyFormat } from "../../utils";
import { dateFormat } from "../../utils/date";
import { downloadCsv } from "../../utils/table-export";
import { renderBusinessName } from "../../utils/render-business-name";
import listClasses from "./List.module.css";
import detailClasses from "./BillingDetails.module.css";
import listSticky from "../../components/list/list.module.css";
import { dueDay, daysUntilDue } from "../../utils/due-day";


type InvoiceTab = "OPEN" | "OVERDUE" | "ALL";

type BillingQuery = {
  tab: InvoiceTab;
  biz: string | null;
  page: number;
  limit: number;
  q: string;
};

const DEFAULT_LIMIT = 40;

function parseTab(raw: string | null): InvoiceTab {
  if (raw === "OVERDUE" || raw === "ALL" || raw === "OPEN") return raw;
  return "OPEN";
}

function readQuery(params: URLSearchParams): BillingQuery {
  return {
    tab: parseTab(params.get("tab")),
    biz: params.get("biz") || null,
    page: Math.max(1, parseInt(params.get("page") || "1", 10) || 1),
    limit: Math.max(1, parseInt(params.get("limit") || String(DEFAULT_LIMIT), 10) || DEFAULT_LIMIT),
    q: params.get("q") || "",
  };
}

function writeQuery(q: BillingQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.tab !== "OPEN") p.set("tab", q.tab);
  if (q.biz) p.set("biz", q.biz);
  if (q.page !== 1) p.set("page", String(q.page));
  if (q.limit !== DEFAULT_LIMIT) p.set("limit", String(q.limit));
  if (q.q) p.set("q", q.q);
  return p;
}

function openCount(c: any): number {
  return Number(c?.invoicesCount || 0) + Number(c?.overdueInvoicesCount || 0);
}

function openBalance(c: any): number {
  return Number(c?.invoicesAmount || 0) + Number(c?.overdueInvoicesAmount || 0);
}

function isUnsupportedTypeError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? "").toLowerCase();
  return (
    msg.includes("type") ||
    msg.includes("valid") ||
    msg.includes("must be one of") ||
    msg.includes("validation") ||
    msg.includes("400")
  );
}

function clientFilterSort(
  rows: any[],
  tab: InvoiceTab,
): any[] {
  let out = rows.filter((r) => {
    if (tab === "ALL") return true;
    const openPay = r.paymentStatus !== "CLOSED";
    if (!openPay) return false;
    if (tab === "OPEN") return r.invoiceStatus === "CONFIRMED";
    // OVERDUE
    return r.invoiceStatus === "CONFIRMED" && r.overdueStatus && r.overdueStatus !== "NORMAL";
  });

  if (tab === "OPEN" || tab === "OVERDUE") {
    out = [...out].sort((a, b) => {
      const da = dueDay(a.paymentDate) ?? "9999-12-31";
      const db = dueDay(b.paymentDate) ?? "9999-12-31";
      if (da !== db) return da < db ? -1 : 1;
      return dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf();
    });
  } else {
    out = [...out].sort(
      (a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf(),
    );
  }
  return out;
}

async function fetchInvoices(opts: {
  businessId: string;
  tab: InvoiceTab;
  page: number;
  limit: number;
  preferNative: boolean;
}): Promise<{ count: number; rows: any[]; usedFallback: boolean }> {
  const { businessId, tab, page, limit, preferNative } = opts;

  if (preferNative) {
    try {
      const data = await NetworkSettlementApi.invoice({
        offset: { page, limit },
        filter: { businessId, type: tab },
      });
      return {
        count: data?.count ?? (data?.rows?.length ?? 0),
        rows: (data?.rows as any[]) ?? [],
        usedFallback: false,
      };
    } catch (err) {
      if (!isUnsupportedTypeError(err)) throw err;
    }
  }

  // DEV fallback: CONFIRMED (+ PENDING for ALL), client filter / sort / page
  const confirmed = await NetworkSettlementApi.invoice({
    offset: { page: 1, limit: 200 },
    filter: { businessId, type: "CONFIRMED" },
  });
  let rows = (confirmed?.rows as any[]) ?? [];

  if (tab === "ALL") {
    try {
      const pending = await NetworkSettlementApi.invoice({
        offset: { page: 1, limit: 200 },
        filter: { businessId, type: "PENDING" },
      });
      const pendingRows = (pending?.rows as any[]) ?? [];
      const seen = new Set(rows.map((r) => r.id));
      for (const r of pendingRows) {
        if (!seen.has(r.id)) rows.push(r);
      }
    } catch {
      /* ignore */
    }
  }

  const filtered = clientFilterSort(rows, tab);
  const start = (page - 1) * limit;
  return {
    count: filtered.length,
    rows: filtered.slice(start, start + limit),
    usedFallback: true,
  };
}

export const CustormerBilling = () => {
  const { t } = useTranslation(["billing", "common", "invoice"]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const myBusinessType = useSelector((state: { auth: IAuth }) => state.auth.myBusinessType);
  const INV_NETWORK_SETTLEMENT_LIST = usePermission("INV_NETWORK_SETTLEMENT_LIST");
  const isSupplierView = myBusinessType !== "BUYER";

  const q = useMemo(() => readQuery(searchParams), [searchParams]);

  const patchQuery = useCallback(
    (patch: Partial<BillingQuery>, resetPage = true) => {
      const next: BillingQuery = {
        ...q,
        ...patch,
        page: resetPage && patch.page === undefined ? 1 : (patch.page ?? q.page),
      };
      setSearchParams(writeQuery(next), { replace: true });
    },
    [q, setSearchParams],
  );

  const [searchInput, setSearchInput] = useState(q.q);
  useEffect(() => {
    setSearchInput(q.q);
  }, [q.q]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      if (searchInput === q.q) return;
      patchQuery({ q: searchInput });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [searchInput, q.q, patchQuery]);

  const networkKey = INV_NETWORK_SETTLEMENT_LIST.hasAccess
    ? `inv.network.${qs.stringify({ page: 1, limit: 200 })}`
    : null;

  const { data: networkData, error: networkError, mutate: reloadNetwork, isLoading: isNetworkLoading } = useSwr(
    networkKey,
    async () =>
      await NetworkSettlementApi.network({
        offset: { page: 1, limit: 200 },
        filter: { regNumber: null, partnerId: null, businessId: null },
      }),
  );

  const customers: any[] = (networkData?.rows as any[]) ?? [];
  const totalCustomers = networkData?.count ?? customers.length;

  const filteredCustomers = useMemo(() => {
    const query = searchInput.trim();
    if (!query) return customers;
    const lowered = query.toLowerCase();
    return customers.filter((c) => {
      const code = (c?.refCode ?? "").toLowerCase();
      const reg = String(c?.regNumber ?? "").toLowerCase();
      return matchesBusinessName(c, query) || code.includes(lowered) || reg.includes(lowered);
    });
  }, [customers, searchInput]);

  // Sync selection: URL biz → first filtered → first overall
  const current: any =
    (q.biz && filteredCustomers.find((c) => c.id === q.biz)) ||
    (q.biz && customers.find((c) => c.id === q.biz)) ||
    filteredCustomers[0] ||
    customers[0] ||
    null;

  useEffect(() => {
    if (!current?.id) return;
    if (q.biz === current.id) return;
    // Only auto-set when nothing selected or selection not in list
    if (!q.biz || !customers.some((c) => c.id === q.biz)) {
      patchQuery({ biz: current.id }, false);
    }
  }, [current?.id, q.biz, customers, patchQuery]);

  const [useNativeTypes, setUseNativeTypes] = useState(true);

  /** Overview panel (canvas): Нээлттэй / Хэтэрсэн / Бүгд tabs, first page only; the rest via "see all". */
  const OVERVIEW_LIMIT = 20;

  const invoiceKey =
    current?.id && INV_NETWORK_SETTLEMENT_LIST.hasAccess
      ? `inv.network.invoice.overview.${current.id}.${q.tab}.${OVERVIEW_LIMIT}.native=${useNativeTypes}`
      : null;

  const { data: invoiceData, error: invoiceError, mutate: reloadInvoices, isLoading: isInvoiceLoading } = useSwr(
    invoiceKey,
    async () => {
      const result = await fetchInvoices({
        businessId: current.id,
        tab: q.tab,
        page: 1,
        limit: OVERVIEW_LIMIT,
        preferNative: useNativeTypes,
      });
      if (result.usedFallback && useNativeTypes) setUseNativeTypes(false);
      return result;
    },
  );

  const invoices: any[] = (invoiceData?.rows ?? []).slice(0, OVERVIEW_LIMIT);
  const invoiceTotal = invoiceData?.count ?? 0;

  const { data: arSummary } = useSwr(
    current?.id ? `ar.summary.billing.${current.id}` : null,
    async () => ARApi.getSummary(current.id),
    { revalidateOnFocus: false },
  );
  const dso =
    typeof arSummary?.metrics?.dso === "number" && Number.isFinite(arSummary.metrics.dso)
      ? arSummary.metrics.dso
      : null;

  const curName = renderBusinessName(current, "-");
  const curRefCode = current?.refCode ?? "-";
  const curReg = current?.regNumber ?? "-";
  const curOpenCount = current ? openCount(current) : 0;
  const curBalance = current ? openBalance(current) : 0;
  const curOverdueAmt = Number(current?.overdueInvoicesAmount || 0);
  const curOverdueCount = Number(current?.overdueInvoicesCount || 0);

  const goInvoiceDetails = (inv: any) => {
    if (!inv?.id) return;
    const closed = inv.invoiceStatus === "CLOSED" || inv.paymentStatus === "CLOSED";
    navigate(closed ? `/closed-invoice/${inv.id}/details` : `/sales-invoice/${inv.id}/details`);
  };

  const renderDue = (inv: any) => {
    if (!inv?.paymentDate) {
      return <Text className={listClasses.amtMuted}>-</Text>;
    }
    const days = (daysUntilDue(inv.paymentDate) ?? 0);
    if (days < 0) {
      return (
        <Text className={listClasses.dueOverdue}>
          {t("common:list.dueOverdue", { days: Math.abs(days) })}
        </Text>
      );
    }
    return (
      <Text className={listClasses.dueRemaining}>
        {t("common:list.dueRemaining", { days })}
      </Text>
    );
  };

  if (!INV_NETWORK_SETTLEMENT_LIST.hasAccess) {
    return <Page>{INV_NETWORK_SETTLEMENT_LIST.accessDenied()}</Page>;
  }

  const statCols = dso != null ? 4 : 3;

  // One row per partner in the list (search applied), the same figures the left column shows.
  const downloadReport = () => {
    const money = (v: number) => currencyFormat(v, { withSymbol: false });
    downloadCsv(
      `${isSupplierView ? "customer" : "supplier"}-billing-${dayjs().format("YYYY-MM-DD")}.csv`,
      [
        t("billing:report.name"),
        t("billing:report.code"),
        t("billing:report.regNumber"),
        t("billing:report.openCount"),
        t("billing:report.balance"),
        t("billing:report.overdueCount"),
        t("billing:report.overdueAmount"),
      ],
      filteredCustomers.map((c) => [
        renderBusinessName(c, "-"),
        c?.refCode,
        c?.regNumber,
        openCount(c),
        money(openBalance(c)),
        Number(c?.overdueInvoicesCount || 0),
        money(Number(c?.overdueInvoicesAmount || 0)),
      ]),
    );
  };

  return (
    <Page>
      <Box className={listClasses.pageHdr}>
        <Box className={listClasses.pageHdrLeft}>
          <Box className={listClasses.pageTitleRow}>
            <Title order={1} className={listClasses.pageTitle}>
              {t(isSupplierView ? "billing:title" : "billing:titleBuyer")}
            </Title>
            {totalCustomers > 0 && (
              <Text component="span" className={listClasses.pageTitleChip}>
                {t(isSupplierView ? "billing:records" : "billing:recordsBuyer", { count: totalCustomers })}
              </Text>
            )}
          </Box>
          <Text className={listClasses.pageSub}>{t(isSupplierView ? "billing:subtitle" : "billing:subtitleBuyer")}</Text>
        </Box>
        <Box className={listClasses.pageHdrRight}>
          <Button
            variant="default"
            className={listClasses.actionBtn}
            disabled={filteredCustomers.length === 0}
            onClick={downloadReport}
          >
            {t("billing:actions.report")}
          </Button>
        </Box>
      </Box>

      <Box className={listClasses.split}>
        <Paper className={listClasses.card} radius="md">
          <Box className={listClasses.searchBar}>
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.currentTarget.value)}
              placeholder={t(isSupplierView ? "billing:customerList.search" : "billing:customerList.searchBuyer")}
              rightSection={<ListMagnifyingGlassIcon color="gray" size={16} />}
              size="sm"
              styles={{ input: { ...filterInputStyles.input, width: "100%", height: 32 } }}
            />
          </Box>
          <Box className={listClasses.customerScroll}>
            {isNetworkLoading && customers.length === 0
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Box key={`sk-${i}`} className={listClasses.customerRow}>
                    <Skeleton h={14} w="55%" mb={6} />
                    <Skeleton h={12} w="40%" />
                  </Box>
                ))
              : filteredCustomers.length === 0
                ? (
                  <ListStates
                    empty
                    error={networkError}
                    onRetry={() => reloadNetwork()}
                    filtered={!!searchInput}
                    query={searchInput}
                    onClear={() => setSearchInput("")}
                  />
                )
                : filteredCustomers.map((c: any) => {
                    const isActive = current?.id === c.id;
                    const name = renderBusinessName(c, "-");
                    const balance = openBalance(c);
                    const overdueCnt = Number(c?.overdueInvoicesCount || 0);
                    const invCnt = openCount(c);
                    return (
                      <UnstyledButton
                        key={c.id ?? c.refCode}
                        className={`${listClasses.customerRow} ${isActive ? listClasses.customerRowActive : ""}`}
                        onClick={() => patchQuery({ biz: c.id ?? null, page: 1 })}
                      >
                        <Box className={listClasses.customerTop}>
                          <Box style={{ flex: 1, minWidth: 0 }}>
                            <Box className={listClasses.avatarName}>{name}</Box>
                            <Box className={listClasses.avatarSub}>
                              <Text component="span" className={listClasses.avatarSubMono}>
                                {c?.refCode ?? "-"}
                              </Text>
                              {` · ${invCnt} ${t("billing:customerList.invoices")}`}
                            </Box>
                          </Box>
                          <Box className={listClasses.customerMetaRight}>
                            <Box className={listClasses.customerAmount}>
                              {currencyFormat(balance, { withSymbol: false })}
                            </Box>
                            {overdueCnt > 0 && (
                              <Box className={listClasses.customerOverdue}>
                                {t("billing:customerList.overdueShort", { count: overdueCnt })}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </UnstyledButton>
                    );
                  })}
          </Box>
        </Paper>

        <Box className={listClasses.detailWrap}>
          {!current ? (
            <Box className={`${listClasses.card} ${listClasses.tblEmpty}`}>
              {t("billing:panel.empty")}
            </Box>
          ) : (
            <>
              <Box className={listClasses.hero}>
                <Box className={listClasses.heroHead}>
                  <Box className={listClasses.heroInfo}>
                    <Text component="div" className={listClasses.heroName}>{curName}</Text>
                    <Text component="div" className={listClasses.heroSub}>
                      <Text component="span" className={listClasses.heroSubMono}>
                        {curRefCode}
                      </Text>
                      {" · "}
                      {t("invoice:detail.meta.ttd")}{" "}
                      <Text component="span" className={listClasses.heroSubMono}>{curReg}</Text>
                    </Text>
                  </Box>
                  <Box className={listClasses.heroActions}>
                    <Button
                      variant="default"
                      className={listClasses.actionBtnSm}
                      leftSection={<PhoneIcon size={12} />}
                    >
                      {t("billing:panel.contact")}
                    </Button>
                    {isSupplierView && (
                      <Button
                        variant="default"
                        className={listClasses.actionBtnSm}
                        leftSection={<PaperPlaneTiltIcon size={12} />}
                      >
                        {t("billing:panel.reminder")}
                      </Button>
                    )}
                    {isSupplierView && (
                      <Button
                        component={Link}
                        to="/sales-invoice/new"
                        className={listClasses.actionBtnPrimarySm}
                        leftSection={<PlusIcon size={12} weight="bold" />}
                      >
                        {t("billing:panel.newInvoice")}
                      </Button>
                    )}
                    <Text
                      component={Link}
                      to={`/customer-billing/${current.id}?tab=invoices&seg=OVERDUE`}
                      state={{ ...current, listQuery: searchParams.toString() }}
                      className={listClasses.detailsLink}
                    >
                      {t("billing:panel.details")}
                    </Text>
                  </Box>
                </Box>

                <Box
                  className={`${listClasses.heroStatRow} ${statCols === 3 ? listClasses.heroStatRow3 : ""}`}
                >
                  <Box className={listClasses.heroStatCell}>
                    <Box className={listClasses.heroStatLbl}>{t("billing:panel.heroStat.open")}</Box>
                    <Box className={listClasses.heroStatVal}>{curOpenCount}</Box>
                  </Box>
                  <Box className={listClasses.heroStatCell}>
                    <Box className={listClasses.heroStatLbl}>{t("billing:panel.heroStat.remaining")}</Box>
                    <Box className={listClasses.heroStatVal}>
                      {currencyFormat(curBalance, { withSymbol: false })}
                    </Box>
                  </Box>
                  <Box className={listClasses.heroStatCell}>
                    <Box className={listClasses.heroStatLbl}>{t("billing:panel.heroStat.overdue")}</Box>
                    <Box
                      className={`${listClasses.heroStatVal} ${curOverdueAmt > 0 ? listClasses.heroStatValDanger : ""}`}
                    >
                      {currencyFormat(curOverdueAmt, { withSymbol: false })}
                    </Box>
                    {curOverdueCount > 0 && (
                      <Box className={listClasses.heroStatSub}>
                        {t("billing:customerList.overdueShort", { count: curOverdueCount })}
                      </Box>
                    )}
                  </Box>
                  {dso != null && (
                    <Box className={listClasses.heroStatCell}>
                      <Box className={listClasses.heroStatLbl}>{t("billing:panel.heroStat.dso")}</Box>
                      <Box className={listClasses.heroStatVal}>
                        {t("billing:ar.avgDays", { days: dso })}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              <Paper className={listClasses.card} radius="md">
                <Box className={listClasses.invoiceHead}>
                  <Box className={detailClasses.segTabs} role="tablist" aria-label={t("billing:panel.history")}>
                    {(["OPEN", "OVERDUE", "ALL"] as InvoiceTab[]).map((value) => (
                      <button
                        key={value}
                        type="button"
                        role="tab"
                        aria-selected={q.tab === value}
                        className={`${detailClasses.segTab} ${q.tab === value ? detailClasses.segTabActive : ""}`}
                        onClick={() => patchQuery({ tab: value })}
                      >
                        {t(`billing:invoiceTab.${value.toLowerCase()}`)}
                      </button>
                    ))}
                  </Box>
                  {q.tab !== "ALL" && <Text className={listClasses.sortHint}>{t("billing:panel.sortHint")}</Text>}
                </Box>

                <Box className={listClasses.tblWrap}>
                  {invoices.length === 0 ? (
                    <ListStates
                      loading={isInvoiceLoading}
                      error={invoiceError}
                      onRetry={() => reloadInvoices()}
                      empty
                      columns={7}
                    />
                  ) : (
                  <Box component="table" className={listClasses.tbl}>
                    <Box component="thead">
                      <Box component="tr" className={listSticky.scrollRow}>
                        <Box
                          component="th"
                          className={`${listClasses.colActions} ${listSticky.stickyActions} ${listSticky.stickyEdge}`}
                        >
                          <Text className={listSticky.stickyActionsHead}>{t("common:actions")}</Text>
                        </Box>
                        <Box component="th">{t("billing:invoiceCol.number")}</Box>
                        <Box component="th">{t("billing:invoiceCol.confirmed")}</Box>
                        <Box component="th">{t("billing:invoiceCol.due")}</Box>
                        <Box component="th" className={listClasses.colNum}>
                          {t("billing:invoiceCol.amount")}
                        </Box>
                        <Box component="th" className={listClasses.colNum}>
                          {t("billing:invoiceCol.paid")}
                        </Box>
                        <Box component="th" className={listClasses.colNum}>
                          {t("billing:invoiceCol.remaining")}
                        </Box>
                      </Box>
                    </Box>
                    <Box component="tbody">
                      {invoices.map((inv: any, i: number) => {
                              const totalAmt = Number(inv.confirmedAmount || 0);
                              const paidAmt = Number(inv.paidAmount || 0);
                              const remainingAmt = Number(
                                inv.amountToPay ?? Math.max(0, totalAmt - paidAmt),
                              );
                              return (
                                <Box
                                  component="tr"
                                  key={inv.id ?? i}
                                  className={listSticky.scrollRow}
                                  onClick={() => goInvoiceDetails(inv)}
                                >
                                  <Box
                                    component="td"
                                    className={`${listClasses.colActions} ${listSticky.stickyActions} ${listSticky.stickyEdge}`}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <RowActions
                                      items={[
                                        {
                                          key: "view",
                                          intent: "view",
                                          icon: <FileTextIcon size={17} />,
                                          label: t("invoice:tooltip.details"),
                                          onClick: () => goInvoiceDetails(inv),
                                        },
                                      ]}
                                    />
                                  </Box>
                                  <Box component="td">
                                    <DocNoCell
                                      value={inv.refCode}
                                      showHash={false}
                                      fz={14}
                                      fw={500}
                                      onClick={() => goInvoiceDetails(inv)}
                                    />
                                  </Box>
                                  <Box component="td" className={listClasses.colMuted}>
                                    {inv.confirmedDate ? dateFormat(inv.confirmedDate) : "-"}
                                  </Box>
                                  <Box component="td">{renderDue(inv)}</Box>
                                  <Box component="td" className={`${listClasses.colNum} ${listClasses.colMuted}`}>
                                    {currencyFormat(totalAmt, { withSymbol: false })}
                                  </Box>
                                  <Box
                                    component="td"
                                    className={`${listClasses.colNum} ${paidAmt > 0 ? listClasses.amtPaid : listClasses.amtMuted}`}
                                  >
                                    {paidAmt > 0 ? currencyFormat(paidAmt, { withSymbol: false }) : "-"}
                                  </Box>
                                  <Box
                                    component="td"
                                    className={`${listClasses.colNum} ${remainingAmt > 0 ? listClasses.amtStrong : listClasses.amtMuted}`}
                                  >
                                    {remainingAmt > 0
                                      ? currencyFormat(remainingAmt, { withSymbol: false })
                                      : "-"}
                                  </Box>
                                </Box>
                              );
                            })}
                    </Box>
                  </Box>
                  )}
                </Box>

                {invoiceTotal > invoices.length && (
                  <Box px="md" py="sm">
                    <Text
                      component={Link}
                      to={`/customer-billing/${current.id}?tab=invoices&seg=${q.tab === "ALL" ? "OPEN" : q.tab}`}
                      state={{ ...current, listQuery: searchParams.toString() }}
                      className={listClasses.detailsLink}
                      style={{ fontSize: 14 }}
                    >
                      {t("billing:detail.seeAllInvoices", { count: invoiceTotal })}
                    </Text>
                  </Box>
                )}
              </Paper>
            </>
          )}
        </Box>
      </Box>
    </Page>
  );
};
